import React, { useState } from "react";
import copy from "clipboard-copy";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "../../Style/CrmDashBoardTable.css";
import OutsideClickHandler from "react-outside-click-handler";
import DataTable from "react-data-table-component";
import {
  FaFileInvoiceDollar,
  FaSearch,
  FaPlus,
  FaCopy,
  FaFileCsv,
  FaFileExcel,
  FaFilePdf,
  FaPrint,
  FaColumns,
} from "react-icons/fa";

const statusColor = (status) => {
  switch (status) {
    case "Accepted":
      return "#28a745";
    case "Sent":
      return "#17a2b8";
    case "Rejected":
    case "Expired":
      return "#dc3545";
    case "Closed":
      return "#6c757d";
    case "Draft":
    default:
      return "#f59e0b";
  }
};

function Quotation({ tableData = [], onCreateClick }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [value, setValue] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [copied, setCopied] = useState(false);
  const [visibile, setVisible] = useState(false);

  const handleSort = (column) => {
    setSortColumn(column);
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  };

  const columns = [
    {
      name: <strong>Subject / Title</strong>,
      selector: (row) => row.subject || row.title || "-",
      sortable: true,
      cell: (row) => (
        <span className="font-weight-bold text-dark">
          {row.subject || row.title || "-"}
        </span>
      ),
    },
    {
      name: <strong>Status</strong>,
      selector: (row) => row.status || "Draft",
      sortable: true,
      cell: (row) => (
        <span
          style={{
            color: "#fff",
            backgroundColor: statusColor(row.status),
            padding: "4px 10px",
            fontSize: "75%",
            fontWeight: "700",
            borderRadius: "0.25rem",
            whiteSpace: "nowrap",
          }}
        >
          {row.status || "Draft"}
        </span>
      ),
    },
    {
      name: <strong>Amount</strong>,
      selector: (row) =>
        row.amount !== undefined && row.amount !== null
          ? `₹ ${Number(row.amount).toLocaleString("en-IN")}`
          : "-",
      sortable: true,
      cell: (row) => (
        <span className="font-weight-bold">
          {row.amount !== undefined && row.amount !== null
            ? `₹ ${Number(row.amount).toLocaleString("en-IN")}`
            : "-"}
        </span>
      ),
    },
    {
      name: <strong>Valid Until</strong>,
      selector: (row) => row.duedate || row.dueDate || row.validUntil || "-",
      sortable: true,
    },
    {
      name: <strong>Created By</strong>,
      selector: (row) => row.createdBy || row.createdby || "-",
      sortable: true,
    },
  ];

  const filteredData = (tableData || [])
    .filter((ele) =>
      value === ""
        ? true
        : Object.values(ele).some((val) =>
            val?.toString().toLowerCase().includes(value.toLowerCase())
          )
    )
    .sort((a, b) => {
      if (!sortColumn) return 0;
      const aValue = a?.[sortColumn] ?? "";
      const bValue = b?.[sortColumn] ?? "";
      return aValue > bValue
        ? sortDirection === "asc"
          ? 1
          : -1
        : aValue < bValue
        ? sortDirection === "asc"
          ? -1
          : 1
        : 0;
    });

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Export helpers
  const handleCopyClick = () => {
    copy(JSON.stringify(tableData, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const convertToCSV = () => {
    const csv = Papa.unparse(tableData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "quotations.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const convertToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Quotations");
    XLSX.writeFile(workbook, "quotations.xlsx");
  };

  const convertToPDF = () => {
    const doc = new jsPDF();
    doc.text(JSON.stringify(tableData, null, 2), 10, 10);
    doc.save("quotations.pdf");
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html><head><title>Quotations</title></head>
      <body><pre>${JSON.stringify(tableData, null, 2)}</pre></body>
      </html>`);
    printWindow.document.close();
    printWindow.print();
  };

  /* ── Toolbar button style ── */
  const toolbarBtnStyle = {
    fontSize: "0.8rem",
    fontWeight: 500,
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
  };

  return (
    <>
      {/* ── Toolbar + Search row ── */}
      <div
        className="d-flex justify-content-between align-items-center flex-wrap px-3 pt-3 pb-2"
        style={{ gap: "10px" }}
      >
        {/* Export buttons */}
        <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
          <button
            className="btn btn-outline-secondary btn-sm"
            style={toolbarBtnStyle}
            onClick={handleCopyClick}
            title="Copy data"
          >
            <FaCopy />
            {copied ? "Copied!" : "Copy"}
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            style={toolbarBtnStyle}
            onClick={convertToCSV}
            title="Export CSV"
          >
            <FaFileCsv style={{ color: "#2e7d32" }} />
            CSV
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            style={toolbarBtnStyle}
            onClick={convertToExcel}
            title="Export Excel"
          >
            <FaFileExcel style={{ color: "#1565c0" }} />
            Excel
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            style={toolbarBtnStyle}
            onClick={convertToPDF}
            title="Export PDF"
          >
            <FaFilePdf style={{ color: "#c62828" }} />
            PDF
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            style={toolbarBtnStyle}
            onClick={handlePrint}
            title="Print"
          >
            <FaPrint />
            Print
          </button>
          <OutsideClickHandler onOutsideClick={() => setVisible(false)}>
            <div className="dropdown position-relative">
              <button
                className="btn btn-outline-secondary btn-sm dropdown-toggle"
                style={toolbarBtnStyle}
                onClick={() => setVisible(!visibile)}
              >
                <FaColumns />
                Columns
              </button>
              <div
                className={`dropdown-menu ${visibile ? "show" : ""} p-2`}
                style={{ minWidth: "160px", right: 0 }}
              >
                {[
                  "Subject",
                  "Status",
                  "Amount",
                  "Valid Until",
                  "Created By",
                ].map((col, idx) => (
                  <div
                    key={idx}
                    className="dropdown-item rounded my-1 text-center py-1"
                    style={{
                      fontSize: "0.82rem",
                      backgroundColor: "#8b5cf6",
                      color: "#fff",
                      cursor: "default",
                    }}
                  >
                    {col}
                  </div>
                ))}
              </div>
            </div>
          </OutsideClickHandler>
        </div>

        {/* Search box */}
        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
          <span
            className="text-muted"
            style={{ fontSize: "0.85rem", fontWeight: 500 }}
          >
            Search:
          </span>
          <div
            className="input-group"
            style={{ width: "280px", flexWrap: "nowrap" }}
          >
            <span
              className="input-group-text"
              style={{
                background: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRight: "none",
                borderRadius: "8px 0 0 8px",
                padding: "0 10px",
              }}
            >
              <FaSearch className="text-secondary" style={{ fontSize: "0.8rem" }} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search quotations..."
              value={value}
              onChange={(e) => setValue(e.target.value)}
              style={{
                border: "1px solid #dee2e6",
                borderLeft: "none",
                borderRadius: "0 8px 8px 0",
                fontSize: "0.875rem",
                height: "34px",
                transition: "box-shadow 0.2s",
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = "0 0 0 3px rgba(139,92,246,0.15)";
                e.target.style.outline = "none";
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = "none";
              }}
            />
          </div>
        </div>
      </div>

      {/* ── Table or Empty State ── */}
      {filteredData.length === 0 ? (
        <div className="text-center py-5 px-3">
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
            style={{ width: 72, height: 72, background: "#ede9fe" }}
          >
            <FaFileInvoiceDollar
              style={{ fontSize: "2rem", color: "#8b5cf6" }}
            />
          </div>
          <h5 className="text-secondary mb-1">No Quotations Found</h5>
          <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
            No quotations are available for this lead yet.
          </p>
          {onCreateClick && (
            <button
              className="btn btn-sm px-4 py-2 rounded-pill text-white fw-medium"
              style={{ backgroundColor: "#8b5cf6", borderColor: "#8b5cf6" }}
              onClick={onCreateClick}
            >
              <FaPlus className="me-1" /> Create Quotation
            </button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <DataTable
            columns={columns}
            data={paginatedData}
            pagination
            paginationServer
            paginationTotalRows={filteredData.length}
            onChangePage={setCurrentPage}
            onChangeRowsPerPage={(newPerPage) => {
              setItemsPerPage(newPerPage);
              setCurrentPage(1);
            }}
            sortServer
            onSort={(column) => handleSort(column.name)}
            highlightOnHover
            striped
          />
        </div>
      )}
    </>
  );
}

export default Quotation;
